import React, { useEffect, useMemo, useState } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import i18n from '../i18n/i18n';

/**
 * Wraps the admin app with an RTL-aware emotion cache. When i18n.language
 * switches to Arabic, MUI components flip their CSS automatically because
 * the stylis-plugin-rtl is installed in the active cache.
 *
 * key={dir} on the inner div forces the subtree to remount when direction
 * changes — otherwise computed margins/paddings linger from the previous
 * direction.
 */
export const RtlGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dir, setDir] = useState<'ltr' | 'rtl'>(
    (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'rtl' : 'ltr',
  );

  useEffect(() => {
    const handler = (lng: string) => {
      setDir(lng.toLowerCase().startsWith('ar') ? 'rtl' : 'ltr');
    };
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, []);

  const cache = useMemo(
    () =>
      createCache({
        key: dir === 'rtl' ? 'mui-rtl' : 'mui',
        stylisPlugins: dir === 'rtl' ? [prefixer, rtlPlugin] : [prefixer],
      }),
    [dir],
  );

  return (
    <CacheProvider value={cache}>
      <div key={dir} dir={dir} style={{ display: 'contents' }}>
        {children}
      </div>
    </CacheProvider>
  );
};
